import { Card } from "@/components/ui/card";
import { LucideIcon } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { cn } from "@/lib/utils";
import { memo } from "react";

interface CategoryCardProps {
  title: string;
  description: string;
  icon: LucideIcon;
  delay?: number;
  link: string;
  equationWord?: string;
  color?: string;
}

// Optimization: Memoize CategoryCard to prevent unnecessary re-renders when parent Categories component
// updates its hover state (hoveredIndex). Since props are stable during these updates, memo prevents
// the entire card sub-tree from re-rendering on every mouse enter/leave event.
const CategoryCard = memo(({ title, description, icon: Icon, delay = 0, link, equationWord, color }: CategoryCardProps) => {
  const { t } = useLanguage();

  // Function to highlight the equation word in the description (only first occurrence)
  const renderDescription = () => {
    if (!equationWord) return description;

    const parts = description.split(new RegExp(`(${equationWord})`, 'gi'));
    let foundFirst = false;

    return (
      <>
        {parts.map((part, index) => {
          if (part.toLowerCase() === equationWord.toLowerCase() && !foundFirst) {
            foundFirst = true;
            return (
              <span key={index} className={`font-bold ${color || 'text-primary'}`}>
                {part}
              </span>
            );
          }
          return part;
        })}
      </>
    );
  };

  return (
    <div
      className="block rounded-lg"
    >
      <Card
        className="group relative overflow-hidden bg-gradient-card backdrop-blur-sm border border-primary/10 p-4 md:p-8 hover:border-primary/30 transition-all duration-500 hover:shadow-card hover:scale-[1.02] cursor-pointer"
        style={{ animationDelay: `${delay}ms` }}
      >
        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-primary opacity-0 group-hover:opacity-10 transition-opacity duration-500" />

        <div className="relative z-10 space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300 shrink-0">
              <Icon className="h-6 w-6 text-primary group-hover:text-primary-glow transition-colors" />
            </div>

            <h3 className={`text-xl md:text-2xl font-bold transition-colors ${color === 'text-blue-500' ? 'group-hover:text-blue-500' :
              color === 'text-purple-500' ? 'group-hover:text-purple-500' :
                color === 'text-green-500' ? 'group-hover:text-green-500' :
                  color === 'text-amber-500' ? 'group-hover:text-amber-500' :
                    'group-hover:text-primary-glow'
              }`}>
              <Link
                to={link}
                title={title}
                className="after:absolute after:inset-0 outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg"
              >
                {title}
              </Link>
            </h3>
          </div>

          <p className="text-muted-foreground leading-relaxed">
            {renderDescription()}
          </p>

          <div
            className={cn(
              buttonVariants({ variant: "ghost" }),
              "group/btn p-0 h-auto font-semibold text-primary hover:text-primary-glow hover:bg-transparent"
            )}
          >
            {t("categories.exploreMore")}
            <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
          </div>
        </div>

        {/* Shine effect on hover */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
        </div>
      </Card>
    </div>
  );
});

CategoryCard.displayName = "CategoryCard";

export default CategoryCard;
